
import { defineComponent } from "vue";
import { Icon, Search, Loading, Empty, Toast, Image, List } from "vant";
import { forkJoin, of, Subscription } from "rxjs";
import { catchError } from "rxjs/operators";
import {
  addChatSearchHistory,
  getChatSearchHistory,
  removeChatSearchHistoryAt,
} from "@/utils/chatSearchHistory";
import { globalSearchByMessage, globalSearchByVisitor } from "@/api/common";
import VisitorSourceLabel from "@/components/VisitorSourceLabel.vue";
import {
  extractVisitorHighlights,
  formatSearchMessageMicrotime,
  isVisitorMessageSender,
  messageItemHighlightParts,
  messageSenderTagLabel,
  splitHighlight,
} from "@/utils/globalSearchNormalize";
import type {
  HighlightPart,
  SearchMessageRow,
  SearchVisitorRow,
  SearchVisitorAvatarFields,
  VisitorHighlightItem,
} from "@/utils/globalSearchNormalize";
import { getVisitorAvatarSrc } from "@/utils/visitorAvatar";

type SearchTab = "message" | "visitor";
type VisitorNamePart = HighlightPart & { dim?: boolean };

const HIDDEN_VISITOR_HIGHLIGHT_FIELDS = new Set([
  "name",
  "nickname",
  "auto_nickname",
]);
const HIDDEN_VISITOR_HIGHLIGHT_LABELS = new Set(["称呼", "昵称"]);

function parseRouteTab(tab: unknown): SearchTab {
  return tab === "visitor" ? "visitor" : "message";
}

function isValidSearchQuery(q: string): boolean {
  return q.length >= 2 && q.length <= 30;
}

export default defineComponent({
  components: {
    VanIcon: Icon,
    VanSearch: Search,
    VanLoading: Loading,
    VanEmpty: Empty,
    VanImage: Image,
    VanList: List,
    VisitorSourceLabel,
  },

  data() {
    return {
      keyword: "",
      historyList: [] as string[],
      lastQuery: "",
      activeTab: "message" as SearchTab,
      messageRows: [] as SearchMessageRow[],
      messageLoading: false,
      messageFinished: false,
      messageCursor: undefined as string | undefined,
      messageRequestCursor: undefined as string | undefined,
      messageTotal: 0,
      messagePageSize: 20,
      messageLoadSub: null as Subscription | null,
      visitorRows: [] as SearchVisitorRow[],
      visitorFieldLabels: {} as Record<string, string>,
      visitorFieldOrder: [] as string[],
      visitorLoading: false,
      visitorFinished: false,
      visitorCursor: undefined as string | undefined,
      visitorRequestCursor: undefined as string | undefined,
      visitorTotal: 0,
      visitorPageSize: 20,
      searchLoading: false,
      searchSub: null as Subscription | null,
      visitorLoadSub: null as Subscription | null,
      /** 已展开全部命中消息的访客 uid */
      expandedMessageUids: {} as Record<string, boolean>,
    };
  },

  computed: {
    currentTabEmpty(): boolean {
      return this.activeTab === "message"
        ? this.messageRows.length === 0
        : this.visitorRows.length === 0;
    },
  },

  mounted() {
    this.historyList = getChatSearchHistory();
    this.applyRouteQuery(true);
  },

  activated() {
    this.applyRouteQuery(false);
  },

  beforeUnmount() {
    this.searchSub?.unsubscribe();
    this.searchSub = null;
    this.visitorLoadSub?.unsubscribe();
    this.visitorLoadSub = null;
    this.messageLoadSub?.unsubscribe();
    this.messageLoadSub = null;
  },

  methods: {
    goBack() {
      this.$router.push({ name: "chatList" });
    },

    setTab(tab: SearchTab) {
      if (this.activeTab === tab) return;
      this.activeTab = tab;
      this.syncRouteQuery();
    },

    handleSearch() {
      const q = this.keyword.trim();
      if (!q) return;
      if (!isValidSearchQuery(q)) {
        Toast.fail("搜索内容需为 2～30 个字符");
        return;
      }
      this.historyList = addChatSearchHistory(q);
      this.lastQuery = q;
      this.activeTab = "message";
      this.syncRouteQuery();
      this.runSearch();
    },

    applyRouteQuery(forceSearch: boolean) {
      const routeQ = String(this.$route.query.q || "").trim();
      const routeTab = parseRouteTab(this.$route.query.tab);

      if (!routeQ) {
        if (!this.lastQuery) return;
        this.keyword = "";
        this.lastQuery = "";
        this.activeTab = "message";
        this.resetSearchResults();
        return;
      }

      if (!isValidSearchQuery(routeQ)) return;

      const queryChanged = routeQ !== this.lastQuery;
      const tabChanged = routeTab !== this.activeTab;

      this.keyword = routeQ;
      this.lastQuery = routeQ;
      this.activeTab = routeTab;

      if (forceSearch || queryChanged) {
        this.runSearch();
      } else if (tabChanged) {
        this.syncRouteQuery();
      }
    },

    syncRouteQuery() {
      if (!this.lastQuery) {
        if (this.$route.query.q || this.$route.query.tab) {
          this.$router
            .replace({ name: "chatSearch", query: {} })
            .catch(() => {});
        }
        return;
      }

      const nextQuery: Record<string, string> = { q: this.lastQuery };
      if (this.activeTab === "visitor") {
        nextQuery.tab = "visitor";
      }

      const curQ = String(this.$route.query.q || "");
      const curTab = String(this.$route.query.tab || "");
      const nextTab = nextQuery.tab || "";
      if (curQ === nextQuery.q && curTab === nextTab) return;

      this.$router
        .replace({ name: "chatSearch", query: nextQuery })
        .catch(() => {});
    },

    resetSearchResults() {
      this.searchSub?.unsubscribe();
      this.searchSub = null;
      this.visitorLoadSub?.unsubscribe();
      this.visitorLoadSub = null;
      this.messageLoadSub?.unsubscribe();
      this.messageLoadSub = null;
      this.searchLoading = false;
      this.messageRows = [];
      this.messageLoading = false;
      this.messageFinished = false;
      this.messageCursor = undefined;
      this.messageRequestCursor = undefined;
      this.messageTotal = 0;
      this.visitorRows = [];
      this.visitorFieldLabels = {};
      this.visitorFieldOrder = [];
      this.visitorLoading = false;
      this.visitorFinished = false;
      this.visitorCursor = undefined;
      this.visitorRequestCursor = undefined;
      this.visitorTotal = 0;
      this.expandedMessageUids = {};
    },

    selectHistory(item: string) {
      this.keyword = item;
      this.handleSearch();
    },

    removeAt(index: number) {
      this.historyList = removeChatSearchHistoryAt(index);
    },

    runSearch() {
      const q = this.lastQuery;
      if (!q) return;

      this.searchSub?.unsubscribe();
      this.visitorLoadSub?.unsubscribe();
      this.messageLoadSub?.unsubscribe();
      this.searchLoading = true;
      this.messageLoading = false;
      this.messageFinished = false;
      this.messageCursor = undefined;
      this.messageRequestCursor = undefined;
      this.messageTotal = 0;
      this.visitorLoading = false;
      this.visitorFinished = false;
      this.visitorCursor = undefined;
      this.visitorRequestCursor = undefined;
      this.visitorTotal = 0;
      this.expandedMessageUids = {};

      this.searchSub = forkJoin({
        message: globalSearchByMessage({ q, size: this.messagePageSize }).pipe(
          catchError((err) => {
            console.error(err);
            Toast.fail("聊天记录搜索失败");
            return of(null);
          }),
        ),
        visitor: globalSearchByVisitor({ q, size: this.visitorPageSize }).pipe(
          catchError((err) => {
            console.error(err);
            Toast.fail("客资信息搜索失败");
            return of(null);
          }),
        ),
      }).subscribe({
        next: ({ message, visitor }: any) => {
          this.searchLoading = false;
          const msgRows = this.pickMessageGroups(message);
          this.messageRows = msgRows;
          this.messageTotal = this.normalizeTotal(
            message?.total,
            this.messageItemsTotalCount(msgRows),
          );
          this.messageCursor = message?.next_cursor;
          this.messageFinished =
            msgRows.length === 0 ||
            !this.messageCursor ||
            this.messageItemsTotalCount(this.messageRows) >= this.messageTotal;

          this.visitorFieldLabels = visitor?.fieldLabels || {};
          this.visitorFieldOrder = visitor?.fieldOrder || [];
          this.visitorRows = visitor?.data || [];
          this.visitorTotal = this.normalizeTotal(
            visitor?.total,
            this.visitorRows.length,
          );
          this.visitorCursor = visitor?.next_cursor;
          this.visitorFinished =
            this.visitorRows.length === 0 ||
            !this.visitorCursor ||
            this.visitorRows.length >= this.visitorTotal;

          if (
            this.activeTab === "message" &&
            this.messageRows.length === 0 &&
            this.visitorRows.length > 0
          ) {
            this.activeTab = "visitor";
            this.syncRouteQuery();
          } else if (
            this.activeTab === "visitor" &&
            this.visitorRows.length === 0 &&
            this.messageRows.length > 0
          ) {
            this.activeTab = "message";
            this.syncRouteQuery();
          }
        },
        error: (err) => {
          console.error(err);
          this.searchLoading = false;
          Toast.fail("搜索失败");
        },
      });
    },

    pickMessageGroups(message: unknown): SearchMessageRow[] {
      if (!message || typeof message !== "object") return [];
      const m = message as Record<string, unknown>;
      const g = m.groups;
      if (Array.isArray(g)) return g as SearchMessageRow[];
      const d = m.data;
      if (Array.isArray(d)) return d as SearchMessageRow[];
      return [];
    },

    /** 所有分组里匹配消息条数之和，用于与接口 total 对齐比较 */
    messageItemsTotalCount(rows: SearchMessageRow[]): number {
      let n = 0;
      for (const row of rows) {
        const items = row.items;
        if (Array.isArray(items)) n += items.length;
      }
      return n;
    },

    onMessageLoad() {
      if (
        this.searchLoading ||
        this.messageLoading ||
        this.activeTab !== "message" ||
        !this.lastQuery ||
        this.messageFinished
      ) {
        this.messageLoading = false;
        return;
      }

      if (!this.messageCursor) {
        this.messageFinished = true;
        this.messageLoading = false;
        return;
      }

      const cursor = this.messageCursor;
      if (!cursor || this.messageRequestCursor === cursor) {
        this.messageLoading = false;
        return;
      }

      this.messageLoading = true;
      this.messageRequestCursor = cursor;
      this.messageLoadSub?.unsubscribe();
      this.messageLoadSub = globalSearchByMessage({
        q: this.lastQuery,
        size: this.messagePageSize,
        cursor,
      })
        .pipe(
          catchError((err) => {
            console.error(err);
            Toast.fail("聊天记录加载失败");
            return of(null);
          }),
        )
        .subscribe((msgRes: unknown) => {
          if (!msgRes) {
            this.messageLoading = false;
            this.messageRequestCursor = undefined;
            return;
          }

          const nextRows = this.pickMessageGroups(msgRes);
          this.messageRows = this.messageRows.concat(nextRows);

          this.messageTotal = this.normalizeTotal(
            (msgRes as Record<string, unknown>)?.total,
            this.messageTotal,
          );
          this.messageCursor = (msgRes as any).next_cursor;
          this.messageFinished =
            nextRows.length === 0 ||
            !this.messageCursor ||
            this.messageItemsTotalCount(this.messageRows) >= this.messageTotal;
          this.messageLoading = false;
          this.messageRequestCursor = undefined;
        });
    },

    onVisitorLoad() {
      if (
        this.searchLoading ||
        this.visitorLoading ||
        this.activeTab !== "visitor" ||
        !this.lastQuery ||
        this.visitorFinished
      ) {
        this.visitorLoading = false;
        return;
      }

      if (!this.visitorCursor) {
        this.visitorFinished = true;
        this.visitorLoading = false;
        return;
      }

      const cursor = this.visitorCursor;
      if (!cursor || this.visitorRequestCursor === cursor) {
        this.visitorLoading = false;
        return;
      }

      this.visitorLoading = true;
      this.visitorRequestCursor = cursor;
      this.visitorLoadSub?.unsubscribe();
      this.visitorLoadSub = globalSearchByVisitor({
        q: this.lastQuery,
        size: this.visitorPageSize,
        cursor,
      })
        .pipe(
          catchError((err) => {
            console.error(err);
            Toast.fail("客资信息加载失败");
            return of(null);
          }),
        )
        .subscribe((visitor: any) => {
          if (!visitor) {
            this.visitorLoading = false;
            this.visitorRequestCursor = undefined;
            return;
          }

          const nextRows = Array.isArray(visitor.data) ? visitor.data : [];
          this.visitorRows = this.visitorRows.concat(nextRows);

          if (Object.keys(this.visitorFieldLabels).length === 0) {
            this.visitorFieldLabels = visitor?.fieldLabels || {};
          }
          if (this.visitorFieldOrder.length === 0) {
            this.visitorFieldOrder = visitor?.fieldOrder || [];
          }

          this.visitorTotal = this.normalizeTotal(
            visitor?.total,
            this.visitorTotal,
          );
          this.visitorCursor = visitor?.next_cursor;
          this.visitorFinished =
            nextRows.length === 0 ||
            !this.visitorCursor ||
            this.visitorRows.length >= this.visitorTotal;
          this.visitorLoading = false;
          this.visitorRequestCursor = undefined;
        });
    },

    normalizeTotal(total: unknown, fallback = 0): number {
      const n = Number(total);
      if (Number.isFinite(n) && n >= 0) return n;
      return fallback;
    },

    formatSearchMessageMicrotime,
    messageItemHighlightParts,
    messageSenderTagLabel,
    isVisitorMessageSender,

    isMessageRowExpanded(uid: string): boolean {
      return !!this.expandedMessageUids[uid];
    },

    messageItemsForRow(row: SearchMessageRow) {
      const items = Array.isArray(row.items) ? row.items : [];
      if (this.isMessageRowExpanded(row.uid) || items.length <= 1) {
        return items;
      }
      return items.slice(0, 1);
    },

    messageRowShowViewAllToggle(row: SearchMessageRow): boolean {
      const items = Array.isArray(row.items) ? row.items : [];
      if (items.length > 1) return true;
      return row.totalHits > 1;
    },

    onToggleAllMessages(row: SearchMessageRow) {
      const items = Array.isArray(row.items) ? row.items : [];
      if (items.length > 1 && row.uid) {
        if (this.isMessageRowExpanded(row.uid)) {
          const nextExpanded = { ...this.expandedMessageUids };
          delete nextExpanded[row.uid];
          this.expandedMessageUids = nextExpanded;
          return;
        }

        this.expandedMessageUids = {
          ...this.expandedMessageUids,
          [row.uid]: true,
        };
        return;
      }
      this.goToMessageConversation(row);
    },

    visitorHighlightItems(row: SearchVisitorRow): VisitorHighlightItem[] {
      return extractVisitorHighlights(
        row.highlight,
        this.visitorFieldLabels,
        this.visitorFieldOrder,
      ).filter((item) => !this.hideVisitorHighlightItem(item));
    },

    hideVisitorHighlightItem(item: VisitorHighlightItem): boolean {
      return (
        HIDDEN_VISITOR_HIGHLIGHT_FIELDS.has(item.field) ||
        HIDDEN_VISITOR_HIGHLIGHT_LABELS.has(item.label)
      );
    },

    showVisitorHighlightLabel(item: VisitorHighlightItem): boolean {
      return !this.hideVisitorHighlightItem(item);
    },

    visitorNameHighlightParts(text: unknown): HighlightPart[] {
      return splitHighlight(String(text || ""), this.lastQuery || this.keyword);
    },

    visitorDisplayNameParts(row: SearchVisitorRow): VisitorNamePart[] {
      const name = String(row.name || "");
      const nickname = String(row.nickname || "");
      if (!name && !nickname) return [];

      if (name && nickname && name !== nickname) {
        return [
          ...this.visitorNameHighlightParts(name),
          { hit: false, text: "(", dim: true },
          ...this.visitorNameHighlightParts(nickname).map((part) => ({
            ...part,
            dim: true,
          })),
          { hit: false, text: ")", dim: true },
        ];
      }

      return this.visitorNameHighlightParts(name || nickname);
    },

    visitorAvatarSrc(row: SearchVisitorAvatarFields): string {
      return getVisitorAvatarSrc(row);
    },

    hasRealtimeConversation(uid: string): boolean {
      const talkList = ((this as any).$store.state as any)?.chat?.talkList;
      return (
        Array.isArray(talkList) &&
        talkList.some((talk: any) => String(talk?.uid || "") === uid)
      );
    },

    messageRouteMicrotime(microtime: unknown): string {
      return String(microtime || "").trim();
    },

    chatSearchReturnQuery(): Record<string, string> {
      const q = String(
        this.$route.query.q || this.lastQuery || this.keyword || "",
      ).trim();

      return {
        from: "chatSearch",
        searchQ: q,
        searchTab: this.activeTab,
      };
    },

    messageConversationQuery(uid: string, microtime = "") {
      return {
        uid,
        ...(microtime ? { microtime } : {}),
        ...this.chatSearchReturnQuery(),
      };
    },

    goToMessageConversation(row: SearchMessageRow, item?: unknown) {
      const uid = String(row.uid || row.visitor?.uid || "").trim();
      if (!uid) {
        Toast.fail("缺少访客标识，无法打开对话");
        return;
      }

      const microtime = this.messageRouteMicrotime(
        (item as { microtime?: unknown } | undefined)?.microtime ??
          row.items?.[0]?.microtime,
      );

      if (this.hasRealtimeConversation(uid)) {
        this.goToChat(uid, microtime);
        return;
      }

      this.goToHistoryDetail(uid, microtime);
    },

    goToChat(uid: string, microtime = "") {
      if (!uid) {
        Toast.fail("缺少访客标识，无法打开对话");
        return;
      }
      this.$router.push({
        path: "/chat",
        query: this.messageConversationQuery(uid, microtime),
      });
    },

    goToHistoryDetail(uid: string, microtime = "") {
      if (!uid) {
        Toast.fail("缺少访客标识，无法打开历史消息");
        return;
      }
      this.$router.push({
        name: "historyDetail",
        query: this.messageConversationQuery(uid, microtime),
      });
    },

    goToLead(uid: string) {
      this.$router.push({
        path: "/edit-lead",
        query: {
          uid,
          from: "chatSearch",
        },
      });
    },
  },
});
